import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../../lib/AuthContext";
import {
  MagnifyingGlass, CircleNotch, ArrowLeft, ArrowRight,
  Eye, BookmarkSimple, DownloadSimple, Lock, Clock,
  CheckCircle, ShieldWarning, Funnel, X,
} from "@phosphor-icons/react";
import { Button } from "../../components/ui/button";

export default function DashboardHistory() {
  const { user, authFetch } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [pecos, setPecos] = useState("all");
  const [oig, setOig] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const hasFilters = pecos !== "all" || oig !== "all" || dateFrom || dateTo;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ search, pecos, oig, page, per_page: 25 });
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const res = await authFetch(`${window.location.origin}/api/dashboard/history?${params}`);
      if (res.ok) {
        const d = await res.json();
        setItems(d.items); setTotal(d.total); setTotalPages(d.total_pages);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [authFetch, search, pecos, oig, dateFrom, dateTo, page]);

  useEffect(() => { load(); }, [load]);

  const clearFilters = () => { setPecos("all"); setOig("all"); setDateFrom(""); setDateTo(""); setPage(1); };

  const tier = user?.tier || "free";

  const exportCsv = () => {
    if (tier === "free") return;
    const hdr = "Provider Name,NPI,Entity Type,Specialty,PECOS Status,OIG Status,Searched At\n";
    const rows = items.map(i =>
      `"${i.provider_name || ""}","${i.npi}","${i.entity_type || ""}","${i.specialty || ""}","${i.pecos_status === true ? "Enrolled" : i.pecos_status === false ? "Not Enrolled" : "Unknown"}","${i.oig_status === true ? "Excluded" : i.oig_status === false ? "Clear" : "Unknown"}","${i.searched_at || ""}"`
    ).join("\n");
    const blob = new Blob([hdr + rows], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "search-history.csv"; a.click();
  };

  const saveProvider = async (item) => {
    try {
      await authFetch(`${window.location.origin}/api/dashboard/saved/add`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          npi: item.npi, provider_name: item.provider_name,
          entity_type: item.entity_type, specialty: item.specialty, state: "",
        }),
      });
    } catch { /* ignore */ }
  };

  return (
    <>
      <Helmet><title>Search History | NPI Lookup</title></Helmet>
      <div className="" data-testid="dashboard-history">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display font-bold text-2xl text-slate-900">Search History</h1>
            <p className="text-sm text-slate-500">{total} searches recorded</p>
          </div>
          {tier === "free" ? (
            <Link to="/pricing"><Button variant="outline" size="sm" className="gap-2 rounded-lg text-slate-500"><Lock className="w-4 h-4" /> Export (Pro+)</Button></Link>
          ) : (
            <Button onClick={exportCsv} variant="outline" size="sm" className="gap-2 rounded-lg"><DownloadSimple className="w-4 h-4" /> Export CSV</Button>
          )}
        </div>

        {/* Search + Filter toggle */}
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" placeholder="Search by name or NPI..." value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal/30"
              data-testid="history-search" />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition-colors ${
              showFilters || hasFilters ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
            }`}
            data-testid="history-filter-toggle"
          >
            <Funnel className="w-4 h-4" /> Filters {hasFilters && <span className="w-1.5 h-1.5 rounded-full bg-teal-400" />}
          </button>
        </div>

        {/* Filter panel */}
        {showFilters && (
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 flex flex-wrap items-end gap-4" data-testid="history-filters">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">PECOS Status</label>
              <select value={pecos} onChange={e => { setPecos(e.target.value); setPage(1); }}
                className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white">
                <option value="all">All</option>
                <option value="enrolled">Enrolled</option>
                <option value="not_enrolled">Not Enrolled</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">OIG Status</label>
              <select value={oig} onChange={e => { setOig(e.target.value); setPage(1); }}
                className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white">
                <option value="all">All</option>
                <option value="clear">Clear</option>
                <option value="excluded">Excluded</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">From</label>
              <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }}
                className="text-sm border border-slate-200 rounded-lg px-3 py-1.5" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">To</label>
              <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }}
                className="text-sm border border-slate-200 rounded-lg px-3 py-1.5" />
            </div>
            {hasFilters && (
              <button onClick={clearFilters} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1 pb-1">
                <X className="w-3 h-3" /> Clear
              </button>
            )}
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center"><CircleNotch className="w-6 h-6 text-teal animate-spin mx-auto" /></div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center">
              <Clock className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <h3 className="font-display font-semibold text-slate-900 mb-1">
                {hasFilters || search ? "No matching results" : "No search history yet"}
              </h3>
              <p className="text-sm text-slate-500">
                {hasFilters || search
                  ? "Try adjusting your filters or search term."
                  : "Your NPI searches will be recorded here automatically."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Provider</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">NPI</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600 hidden md:table-cell">Type</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600 hidden lg:table-cell">Specialty</th>
                    <th className="text-center px-4 py-3 font-semibold text-slate-600">PECOS</th>
                    <th className="text-center px-4 py-3 font-semibold text-slate-600">OIG</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Searched</th>
                    <th className="text-right px-4 py-3 font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, i) => (
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="px-4 py-3">
                        <Link to={`/provider/${item.npi}`} className="font-medium text-slate-900 hover:text-teal">
                          {item.provider_name || "—"}
                        </Link>
                      </td>
                      <td className="px-4 py-3 font-mono text-slate-600 text-xs">{item.npi}</td>
                      <td className="px-4 py-3 text-slate-500 hidden md:table-cell">
                        {item.entity_type === "NPI-1" ? "Individual" : item.entity_type === "NPI-2" ? "Organization" : "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-600 max-w-[160px] truncate hidden lg:table-cell">{item.specialty || "—"}</td>
                      <td className="px-4 py-3 text-center"><StatusBadge value={item.pecos_status} trueLabel="Enrolled" falseLabel="Not Enrolled" /></td>
                      <td className="px-4 py-3 text-center"><StatusBadge value={item.oig_status} trueLabel="EXCLUDED" falseLabel="Clear" isWarning /></td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">
                        {item.searched_at ? new Date(item.searched_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Link to={`/provider/${item.npi}`} className="text-slate-400 hover:text-teal" title="View Profile"><Eye className="w-4 h-4" /></Link>
                          <button onClick={() => saveProvider(item)} className="text-slate-400 hover:text-teal" title="Save Provider"><BookmarkSimple className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 text-sm text-slate-500">
            <span>Page {page} of {totalPages}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="gap-1 rounded-lg"><ArrowLeft className="w-4 h-4" /> Prev</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="gap-1 rounded-lg">Next <ArrowRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function StatusBadge({ value, trueLabel, falseLabel, isWarning }) {
  if (value === null || value === undefined) return <span className="text-[11px] text-slate-400">—</span>;
  if (isWarning) {
    return value
      ? <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-700 text-[11px] font-semibold rounded-full"><ShieldWarning className="w-3 h-3" weight="fill" />{trueLabel}</span>
      : <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[11px] font-semibold rounded-full"><CheckCircle className="w-3 h-3" weight="fill" />{falseLabel}</span>;
  }
  return value
    ? <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[11px] font-semibold rounded-full"><CheckCircle className="w-3 h-3" weight="fill" />{trueLabel}</span>
    : <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-500 text-[11px] font-semibold rounded-full">{falseLabel}</span>;
}
